/**
 * Per-tab journey id for forensic path tracing (Track A / QPN-2102).
 *
 * Continuity rules:
 * - Prefer sessionStorage so a full page refresh keeps the same id.
 * - Once resolved for this JS realm, the id is immutable in-memory (ignores
 *   later sessionStorage overwrites).
 * - If sessionStorage throws, keep a single in-memory id for the tab lifetime
 *   and warn once (do not mint a new id on every getJourneyId call).
 *
 * Identity rules:
 * - Only RFC 4122 UUIDs (version 1–5, IETF variant) are accepted.
 * - Generated via crypto.randomUUID, or crypto.getRandomValues UUID v4 fallback.
 */

const STORAGE_KEY = 'qapath_journey_id';
/** RFC 4122: version nibble 1–5, variant nibble 8|9|a|b */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** @type {string|null} Locked id for this tab/JS realm after first resolve. */
let lockedJourneyId = null;
let storageFailureWarned = false;
let secureRandomMissingWarned = false;

class SecureRandomUnavailableError extends Error {
  constructor(message = '[journeyId] Secure random source unavailable') {
    super(message);
    this.name = 'SecureRandomUnavailableError';
  }
}

function bytesToUuidV4(bytes) {
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function createIdFromGetRandomValues() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return bytesToUuidV4(bytes);
}

/**
 * Mint a collision-resistant journey id (UUID v4).
 * Throws SecureRandomUnavailableError if no CSPRNG is available.
 * @returns {string}
 */
function createId() {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto.getRandomValues === 'function') {
      return createIdFromGetRandomValues();
    }
  }
  if (!secureRandomMissingWarned) {
    secureRandomMissingWarned = true;
    // eslint-disable-next-line no-console
    console.error(
      '[journeyId] Secure random source unavailable (crypto.randomUUID / getRandomValues)'
    );
  }
  throw new SecureRandomUnavailableError();
}

function isValidJourneyId(value) {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

function warnStorageFailure(error) {
  if (storageFailureWarned) return;
  storageFailureWarned = true;
  // eslint-disable-next-line no-console
  console.warn(
    '[journeyId] sessionStorage unavailable; using in-memory journey id for this tab only',
    error
  );
}

/**
 * Returns the current tab's journey id.
 * Immutable for the lifetime of this module instance after first call.
 * @returns {string}
 */
export function getJourneyId() {
  if (lockedJourneyId) {
    return lockedJourneyId;
  }

  let existing = null;
  try {
    existing = sessionStorage.getItem(STORAGE_KEY);
  } catch (storageError) {
    warnStorageFailure(storageError);
    const next = createId();
    lockedJourneyId = next;
    return next;
  }

  if (isValidJourneyId(existing)) {
    lockedJourneyId = existing;
    return existing;
  }

  const next = createId();
  try {
    sessionStorage.setItem(STORAGE_KEY, next);
  } catch (storageError) {
    warnStorageFailure(storageError);
    // Still use the minted id for this tab; persistence just failed.
  }

  lockedJourneyId = next;
  return next;
}

/**
 * Test-only: clear the in-memory lock so suites can re-init.
 * @returns {void}
 */
export function __resetJourneyIdForTests() {
  lockedJourneyId = null;
  storageFailureWarned = false;
  secureRandomMissingWarned = false;
}

export const JOURNEY_ID_HEADER = 'X-Journey-Id';
export { SecureRandomUnavailableError };
